import React, { useState, useCallback, useMemo } from "react";
import { Drawer, Button, Col, Row, Form } from "antd";
import { connect } from "dva";
import { VehicleType, VehicleColor } from "@/utils/enum";
import { uploadGatePass } from "@/utils/gatepass";
import { getUrlParams, parseAsNumber, parseVehicleData } from "@/utils/utils";
import useScreenDetector from "@/hooks/useScreenDetector";
import DrawerWrapper from "@/components/DrawerWrapper";
import VehicleForm from "@/forms/shared/Vehicle/VehicleForm";

const EditVehicleMenuItem = ({
  configPerm,
  dealerId,
  dispatch,
  visible,
  vehicleData,
  shipmentId: propShipmentId,
  customSubmitFn = null,
  hideVehicleEditDrawer,
}) => {
  const [editVehicleFormRef] = Form.useForm();

  const { isMobile } = useScreenDetector();

  const [loading, setLoading] = useState(false);

  const onClose = useCallback(() => {
    if (typeof hideVehicleEditDrawer === "function") {
      hideVehicleEditDrawer();
    }
    editVehicleFormRef?.resetFields();
  }, [hideVehicleEditDrawer, editVehicleFormRef]);

  const onSubmit = useCallback(
    async data => {
      const shipmentId = propShipmentId || getUrlParams("shipmentId"); // if shipment id in props, then this component is in modal in admin shipments, else in admin shipment view

      let gatePassPayload = data?.gatePassUrl;

      // if we have a file to upload
      if (data?.gatePassFile) {
        gatePassPayload = await uploadGatePass(data.gatePassFile);
      }

      const usingCustomVehicleColor = data.color === VehicleColor.Other;

      const vehicleId = vehicleData.id;

      const payload = {
        shipmentId,
        vehicleId,
        dealerId,
        vin: data.vin?.toUpperCase(),
        stockNumber: data.stockNumber,
        year: data.year,
        make: data.make,
        model: data.model,
        color: usingCustomVehicleColor ? data.customColor : data.color,
        vehicleType: data.vehicleType,
        drivabilityType: data.inoperational,
        otherType: data.vehicleType === VehicleType.Other ? data.vehicleTypeComment : "",
        comments: data.comments,
        curbWeight: data.curbWeight,
        trimPackage: data.trimPackageName,
        bodySubType: data.bodySubType,
        // exterior dimension fields
        width: parseAsNumber(data?.width, 0),
        height: parseAsNumber(data?.height, 0),
        length: parseAsNumber(data?.length, 0),
        exteriorDimensions: data.exteriorDimensions,
        truckWithLongWheelBase: data.vehicleType === VehicleType.Truck ? data.truckWithLongWheelBase : false,
        truckWithDualWheels: data.vehicleType === VehicleType.Truck ? data.truckWithDualWheels : false,
        gatePassType: data.gatePassStatus,
        gatePassUrl: gatePassPayload,
        isEV: data.isEV,
      };

      setLoading(true);

      // DISCLAIMER: We cannot always rely on dispatching `adminShipmentView/editVehicle` here, because it requires
      // loading the adminShipmentView model first, and to do that you need to open a shipment details, if the user
      // goes to Admin Tools -> Shipments -> Click on "View Vehicles: n" of a item without looking at any shipment
      // details, the dispatch will never reach the function
      let caller;
      if (typeof customSubmitFn === "function") {
        caller = customSubmitFn(payload, vehicleId);
      } else {
        caller = dispatch({
          type: `adminShipmentView/editVehicle`,
          shipmentId,
          vehicleId,
          payload,
        });
      }

      if (typeof caller.then === "function") {
        caller
          .then(response => {
            if (!response.error) {
              onClose({ updatedInfo: payload });
            }
          })
          .finally(() => {
            setLoading(false);
          });
      }
    },
    [propShipmentId, vehicleData, dispatch, customSubmitFn, dealerId, onClose],
  );

  const vehicleFormData = useMemo(() => parseVehicleData(vehicleData), [vehicleData]);

  return (
    <DrawerWrapper visible={visible}>
      <Drawer
        title="Edit vehicle"
        width={isMobile ? "80%" : "40%"}
        onClose={onClose}
        visible={visible}
        bodyStyle={{ paddingBottom: 80 }}
        footer={
          <Row>
            <Col flex={1}>
              <div style={{ textAlign: "right" }}>
                <Button onClick={onClose} style={{ marginRight: 8 }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" onClick={editVehicleFormRef.submit} loading={loading}>
                  Save
                </Button>
              </div>
            </Col>
          </Row>
        }
      >
        {visible && (
          <VehicleForm
            formRef={editVehicleFormRef}
            defaultValues={vehicleFormData}
            configPerm={configPerm}
            shipmentStatus={vehicleData?.shipment?.status}
            onSubmit={onSubmit}
            editMode
          />
        )}
      </Drawer>
    </DrawerWrapper>
  );
};

export default connect(({ configs, adminShipmentView }) => ({
  configPerm: configs.configPerm,
  dealerId: adminShipmentView?.data?.dealer?.id,
}))(EditVehicleMenuItem);
